import * as React from 'react'
import { Route, Routes } from 'react-router-dom'

import {
  CompetenciesCompetencyCrumbDocument,
  CompetenciesCrumbDocument,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import i18n from 'modules/core/lib/i18n'
import { buildNavigation } from 'modules/core/lib/react-router'

import CompetencyHeader from 'modules/competencies/components/CompetenciesHeader'
import { buildCompetenciesConfig } from 'modules/competencies/hooks/useCompetenciesFeatures'

const RootPage = React.lazy(() => import('modules/competencies/pages/RootPage'))
const CompetenciesListPage = React.lazy(() => import('modules/competencies/pages/CompetenciesList'))
const CompetenciesGroupsPage = React.lazy(
  () => import('modules/competencies/pages/CompetencyGroups')
)
const CompetencyPageRedirect = React.lazy(
  () => import('modules/competencies/pages/CompetencyPageRedirect')
)
const CompetencyLevelsPage = React.lazy(() => import('modules/competencies/pages/CompetencyLevels'))
const CompetencyJobRolesPage = React.lazy(
  () => import('modules/competencies/pages/CompetencyRoleLevelsPage')
)

const CompetencySettingsPage = React.lazy(
  () => import('modules/competencies/pages/CompetencySettings')
)
const CompetenciesImporterPage = React.lazy(
  () => import('modules/competencies/pages/CompetenciesImporter')
)

export const competenciesRoutes = buildNavigation((navigation) => {
  return [
    {
      // TODO: this replace is used until we move all the navigation items
      // of the modules under performance_v2 to the performance_v2 navigation
      path: navigation.competencies.path.replace(`${navigation.performanceProcess.path}/`, ''),
      element: <RootPage />,
      handle: {
        meta: ({ watchQuery }) => {
          return watchQuery({
            query: CompetenciesCrumbDocument,
          }).map(({ data }) => {
            const canManageCompetencies =
              data?.competencies?._policies?.competenciesManage?.can ?? false

            if (!canManageCompetencies) return {}

            return {
              title: i18n.t('competencies_v3.title'),
              illustration: 'window-user',
              description: i18n.t('competencies_v3.description'),
              subNavigation: [
                {
                  index: true,
                  title: i18n.t('competencies_v3.main_tabs.company_competencies'),
                  href: navigation.competencies.fullPath(),
                },
                {
                  title: i18n.t('competencies_v3.main_tabs.groups'),
                  href: navigation.competencies.groups.fullPath(),
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <CompetenciesListPage />,
        },
        {
          path: navigation.competencies.groups.path,
          element: <CompetenciesGroupsPage />,
        },
        {
          path: navigation.competencies.import.path,
          element: (
            <Routes>
              <Route path="" element={<CompetenciesImporterPage />} />
            </Routes>
          ),
          handle: {
            resource: true,
            meta: () => ({
              title: i18n.t('competencies_v3.import'),
            }),
          },
        },
        {
          path: navigation.competencies.competencyId.path,
          handle: {
            resource: true,
            meta: ({ watchQuery, params, session }) => {
              if (!params['competencyId']) {
                return { title: '' }
              }

              return watchQuery({
                query: CompetenciesCompetencyCrumbDocument,
                variables: {
                  id: Number(params['competencyId']),
                  companyId: session.company.id,
                },
              }).map(({ data }) => {
                const canManageCompetencies =
                  data?.competencies?._policies?.competenciesManage?.can ?? false

                if (!canManageCompetencies) return {}

                const competency = data.competencies.competency

                if (!competency || !competency.name) {
                  return {}
                }

                const features = data?.features?.featuresConnection?.nodes || []

                const canSeeJobCatalog =
                  data?.jobCatalog?._policies?.jobCatalogTreeSee?.can ?? false

                const featuresConfig = buildCompetenciesConfig(features)
                const { isJobCatalogTreeEnabled } = featuresConfig

                return {
                  title: competency.name,
                  headerComponent: (
                    <CompetencyHeader
                      competency={competency}
                      assignedJobsCount={competency.jobCatalogRoleLevels?.nodeUuids.length ?? 0}
                      featuresConfig={featuresConfig}
                      canManageCompetencies={canManageCompetencies}
                      canSeeJobCatalog={canSeeJobCatalog}
                    />
                  ),
                  subNavigation: compact([
                    isJobCatalogTreeEnabled && {
                      index: true,
                      title: i18n.t('competencies_v3.levels.title'),
                      href: navigation.competencies.competencyId.levels.fullPath({
                        competencyId: competency.id,
                      }),
                    },
                    isJobCatalogTreeEnabled &&
                      canSeeJobCatalog && {
                        title: i18n.t('competencies_v3.header.jobs'),
                        href: navigation.competencies.competencyId.jobRoles.fullPath({
                          competencyId: competency.id,
                        }),
                      },
                  ]),
                }
              })
            },
          },
          children: [
            {
              index: true,
              element: <CompetencyPageRedirect />,
            },
            {
              path: navigation.competencies.competencyId.levels.path,
              element: <CompetencyLevelsPage />,
            },
            {
              path: navigation.competencies.competencyId.jobRoles.path,
              element: <CompetencyJobRolesPage />,
            },
            {
              path: navigation.competencies.competencyId.settings.path,
              element: <CompetencySettingsPage />,
            },
          ],
        },
      ],
    },
  ]
})
